import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ImageOff, Plus, Star, Tags, Upload } from "lucide-react";
import * as React from "react";

import { ApiError, assetUrl } from "@/api/client";
import { catalog } from "@/api/endpoints";
import type { Category } from "@/api/types";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  EmptyState,
  ErrorState,
  FormError,
  Spinner,
  TableSkeleton,
} from "@/components/ui/feedback";
import { Input, Label, Textarea } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";

const ICON_ACCEPT = "image/png,image/jpeg,image/webp";
const ICON_HINT =
  "Optional. PNG, JPEG or WebP up to 5 MB — a square picture on a transparent background works best. Shown at 256 × 256.";

export default function Catalog() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [creating, setCreating] = React.useState(false);

  const query = useQuery({ queryKey: ["categories"], queryFn: catalog.categories });

  const update = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof catalog.setMerchandising>[1];
    }) => catalog.setMerchandising(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
    onError: (err) =>
      toast.error(
        "Could not update the category",
        err instanceof ApiError ? err.message : undefined,
      ),
  });

  // Sort by the order the app will render them in, then by name for stability.
  const sorted = React.useMemo(() => {
    const rows = [...(query.data ?? [])];
    rows.sort(
      (a, b) =>
        (a.display_order ?? 0) - (b.display_order ?? 0) ||
        a.name.localeCompare(b.name),
    );
    return rows;
  }, [query.data]);

  function move(index: number, direction: -1 | 1) {
    const target = sorted[index + direction];
    const current = sorted[index];
    if (!target || !current) return;
    // Swap the two display_order values.
    update.mutate({
      id: current.id,
      body: { display_order: target.display_order ?? index + direction },
    });
    update.mutate({
      id: target.id,
      body: { display_order: current.display_order ?? index },
    });
  }

  return (
    <>
      <PageHeader
        title="Categories"
        description="Controls what farmers see and in what order. Changes reach the app immediately — no release needed."
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus />
            Add category
          </Button>
        }
      />

      <Card className="overflow-hidden">
        {query.isLoading ? (
          <TableSkeleton cols={6} />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : sorted.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Order</TableHead>
                <TableHead className="w-28">Icon</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Featured</TableHead>
                <TableHead>Visible in app</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((c: Category, i) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7"
                        disabled={i === 0 || update.isPending}
                        onClick={() => move(i, -1)}
                        aria-label={`Move ${c.name} up`}
                      >
                        <ArrowUp className="size-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7"
                        disabled={i === sorted.length - 1 || update.isPending}
                        onClick={() => move(i, 1)}
                        aria-label={`Move ${c.name} down`}
                      >
                        <ArrowDown className="size-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    <IconCell category={c} />
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{c.name}</div>
                    {c.name_hi && (
                      <div className="text-xs text-muted-foreground">{c.name_hi}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{c.slug}</TableCell>
                  <TableCell>
                    <Button
                      variant={c.is_featured ? "default" : "outline"}
                      size="sm"
                      disabled={update.isPending}
                      onClick={() =>
                        update.mutate({
                          id: c.id,
                          body: { is_featured: !c.is_featured },
                        })
                      }
                    >
                      <Star className={c.is_featured ? "fill-current" : ""} />
                      {c.is_featured ? "Featured" : "Feature"}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <button
                      type="button"
                      disabled={update.isPending}
                      onClick={() =>
                        update.mutate({
                          id: c.id,
                          body: { is_active: !c.is_active },
                        })
                      }
                      className="disabled:opacity-50"
                    >
                      <Badge variant={c.is_active ? "good" : "default"}>
                        {c.is_active ? "Visible" : "Hidden"}
                      </Badge>
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <EmptyState
            icon={Tags}
            title="No categories yet"
            description="Add the first one to let providers list their services."
          />
        )}
      </Card>

      {creating && <CreateCategoryDialog onClose={() => setCreating(false)} />}
    </>
  );
}

function CreateCategoryDialog({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [name, setName] = React.useState("");
  const [nameHi, setNameHi] = React.useState("");
  const [slug, setSlug] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [visible, setVisible] = React.useState(true);
  const [icon, setIcon] = React.useState<File | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const category = await catalog.createCategory({
        name: name.trim(),
        // Blank optional fields are left out; the server derives the slug from the name.
        name_hi: nameHi.trim() || undefined,
        slug: slug.trim() || undefined,
        description: description.trim() || undefined,
        is_active: visible,
      });
      if (!icon) return category;
      try {
        return await catalog.uploadIcon(category.id, icon);
      } catch (err) {
        // The category exists either way; say so rather than offering a retry
        // that would fail on the now-taken slug.
        toast.error(
          "Category created, but the icon was not saved",
          err instanceof ApiError ? `${err.message} Add it from the Icon column.` : undefined,
        );
        return category;
      }
    },
    onSuccess: (category) => {
      toast.success("Category created", category.name);
      void queryClient.invalidateQueries({ queryKey: ["categories"] });
      onClose();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Could not create the category."),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a category</DialogTitle>
          <DialogDescription>
            Providers file their services under it, and farmers browse by it.
          </DialogDescription>
        </DialogHeader>

        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            create.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="category-name">Name</Label>
            <Input
              id="category-name"
              required
              minLength={2}
              maxLength={120}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Tractor Hire"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="category-name-hi">Name in Hindi</Label>
            <Input
              id="category-name-hi"
              maxLength={120}
              value={nameHi}
              onChange={(e) => setNameHi(e.target.value)}
              placeholder="ट्रैक्टर किराया"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="category-slug">Slug</Label>
            <Input
              id="category-slug"
              maxLength={140}
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="tractor-hire"
            />
            <p className="text-[11px] text-muted-foreground">
              Optional — made from the name if left blank. It cannot be changed later.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="category-description">Description</Label>
            <Textarea
              id="category-description"
              maxLength={1000}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="category-icon">Icon</Label>
            <input
              id="category-icon"
              type="file"
              accept={ICON_ACCEPT}
              onChange={(e) => setIcon(e.target.files?.[0] ?? null)}
              className="block w-full text-xs text-muted-foreground file:mr-3 file:rounded-md file:border file:border-border file:bg-card file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-foreground hover:file:bg-accent"
            />
            <p className="text-[11px] text-muted-foreground">{ICON_HINT}</p>
          </div>

          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={visible}
              onChange={(e) => setVisible(e.target.checked)}
              className="size-3.5 accent-[hsl(var(--primary))]"
            />
            Visible in the app right away
          </label>

          <FormError message={error} />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending && <Spinner />}
              Create category
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function IconCell({ category }: { category: Category }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const inputRef = React.useRef<HTMLInputElement>(null);

  const upload = useMutation({
    mutationFn: (file: File) => catalog.uploadIcon(category.id, file),
    onSuccess: () => {
      toast.success("Icon updated", category.name);
      void queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
    onError: (err) =>
      toast.error(
        "Could not upload the icon",
        err instanceof ApiError ? err.message : undefined,
      ),
  });

  return (
    <div className="flex items-center gap-2">
      <div className="flex size-10 items-center justify-center overflow-hidden rounded-md border border-border bg-muted">
        {category.icon_url ? (
          <img
            src={assetUrl(category.icon_url)}
            alt=""
            className="size-full object-contain"
          />
        ) : (
          <ImageOff className="size-4 text-muted-foreground" aria-hidden />
        )}
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="size-7"
        disabled={upload.isPending}
        onClick={() => inputRef.current?.click()}
        aria-label={`${category.icon_url ? "Replace" : "Add"} icon for ${category.name}`}
        title={ICON_HINT}
      >
        {upload.isPending ? <Spinner /> : <Upload className="size-3.5" />}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept={ICON_ACCEPT}
        className="sr-only"
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Reset so picking the same file again still fires onChange.
          e.target.value = "";
          if (file) upload.mutate(file);
        }}
      />
    </div>
  );
}
